/**
 * Italian date helpers for flatpickr + API boundary.
 *
 * API/MySQL: Y-m-d
 * UI (flatpickr dateFormat): d/m/Y
 *
 * Without explicit parseDate, flatpickr mis-reads ISO strings and can
 * fall back to the current year (client report: birthday year → 2023/2026).
 */

/** Convert API `Y-m-d` (or datetime) → `d/m/Y` for display. */
export function isoToIt(value) {
  if (value === null || value === undefined || value === '') return ''
  const s = String(value).trim()
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[3]}/${m[2]}/${m[1]}`
  // already d/m/Y
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s)) return s.split(/\s/)[0]
  return s
}

/** Convert `d/m/Y` → `Y-m-d` (pass-through if already ISO). */
export function itToIso(value) {
  if (value === null || value === undefined || value === '') return ''
  const s = String(value).trim()
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const it = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (it) {
    const d = it[1].padStart(2, '0')
    const m = it[2].padStart(2, '0')
    return `${it[3]}-${m}-${d}`
  }
  return s
}

/**
 * flatpickr config: d/m/Y UI, accepts ISO on load, never invents current year
 * from a failed parse of Y-m-d.
 */
export function italianFlatpickrConfig(extra = {}) {
  return {
    dateFormat: 'd/m/Y',
    allowInput: true,
    parseDate(datestr) {
      if (!datestr) return undefined
      const s = String(datestr).trim()
      let y
      let m
      let d
      let match = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (match) {
        y = +match[1]
        m = +match[2]
        d = +match[3]
      } else {
        match = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/)
        if (!match) return undefined
        d = +match[1]
        m = +match[2]
        y = +match[3]
      }
      if (y < 1900 || y > 2200) return undefined
      if (m < 1 || m > 12 || d < 1 || d > 31) return undefined
      const dt = new Date(y, m - 1, d)
      // reject overflow (e.g. 31/02 → March)
      if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
        return undefined
      }
      return dt
    },
    formatDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
      const d = String(date.getDate()).padStart(2, '0')
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const y = date.getFullYear()
      return `${d}/${m}/${y}`
    },
    ...extra,
  }
}
